import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Alert,
  Spinner,
  Progress,
  Badge,
  Carousel,
  CarouselItem,
  CarouselControl,
  CarouselIndicators,
  CarouselCaption,
} from 'reactstrap';
import CreatableSelect from 'react-select/creatable';
import TimeSelect from './TimeSelect';
import { useDropzone } from 'react-dropzone';
import FeatherIcon from 'feather-icons-react';
import {
  requestUploadUrl,
  confirmUpload,
  getAttachmentSignedUrls,
  sendSnowLogNotification,
} from './FormikHelpers/Api';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import jwt_decode from 'jwt-decode';
import { SnowIceRemovalLogValidationSchema, validateStep } from './FormikHelpers/ValidationSchema';
import { setLoading } from '../../../../../store/actions';
import { useDispatch } from 'react-redux';

const ALLOWED_FILE_TYPES = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska';
const MAX_FILE_SIZE = 120 * 1024 * 1024;
const MAX_FILES = 10;

const DEFAULT_ACTIONS = [
  'Snow Removal',
  'Blading',
  'Ice Scraping',
  'Snow Haul',
  'Sanding',
  'Salting',
  'Push and Pile',
  'Parking Lot Sand',
];

const AttachmentCarouselModal = ({ isOpen, toggle, items, initialIndex = 0 }) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [animating, setAnimating] = useState(false);

  const next = () => {
    if (animating) return;
    setActiveIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  };

  const previous = () => {
    if (animating) return;
    setActiveIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  };

  const goToIndex = (newIndex) => {
    if (animating) return;
    setActiveIndex(newIndex);
  };

  if (!isOpen || !items?.length) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 9999 }}>
      <ModalHeader toggle={toggle} className="border-0 pb-2 bg-dark text-white">
        <div className="d-flex align-items-center gap-3">
          <FeatherIcon icon="image" size={22} />
          <span>Supporting Media Preview ({items.length} items)</span>
        </div>
      </ModalHeader>
      <ModalBody className="p-0 bg-dark">
        <Carousel
          activeIndex={activeIndex}
          next={next}
          previous={previous}
          pause={false}
          interval={false}
          className="mb-0"
        >
          <CarouselIndicators
            items={items}
            activeIndex={activeIndex}
            onClickHandler={goToIndex}
          />
          {items.map((item, idx) => (
            <CarouselItem
              onExiting={() => setAnimating(true)}
              onExited={() => setAnimating(false)}
              key={item.src || idx}
            >
              <div className="text-center bg-dark" style={{ minHeight: '60vh' }}>
                {item.type === 'video' ? (
                  <video
                    src={item.src}
                    controls
                    autoPlay
                    style={{
                      maxHeight: '70vh',
                      width: '100%',
                      objectFit: 'contain',
                      borderRadius: '8px',
                    }}
                  />
                ) : (
                  <img
                    src={item.src}
                    alt={item.caption}
                    style={{
                      maxHeight: '70vh',
                      width: '100%',
                      objectFit: 'contain',
                      borderRadius: '8px',
                    }}
                  />
                )}
              </div>
              <CarouselCaption
                captionText={item.caption}
                className="bg-dark bg-opacity-75 text-white p-3"
              />
            </CarouselItem>
          ))}
          <CarouselControl direction="prev" directionText="Previous" onClickHandler={previous} />
          <CarouselControl direction="next" directionText="Next" onClickHandler={next} />
        </Carousel>
      </ModalBody>
      <ModalFooter className="border-0 bg-dark">
        <Button color="light" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const GenerateSnowIceRemovalLogModal = ({
  isModalOpen,
  setIsModalOpen,
  formik,
  filters,
  isEdit = false,
  onRefresh = () => { },
  submitSnowLog,
}) => {
  const dispatch = useDispatch();

  const [pendingFiles, setPendingFiles] = useState([]);
  const [signedUrlCache, setSignedUrlCache] = useState({});
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const [userRole, setUserRole] = useState(null);
  const [customActions, setCustomActions] = useState([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, fileName: '' });
  const [customActionInput, setCustomActionInput] = useState('');

  const handleAddCustomAction = () => {
    const newAction = customActionInput.trim();
    if (!newAction) return;

    if (actionOptions.some((opt) => opt.value === newAction)) {
      toast.info('This action already exists');
      return;
    }

    setCustomActions((prev) => [...prev, newAction]);
    formik.setFieldValue('actions_performed', [
      ...(formik.values.actions_performed || []),
      newAction,
    ]);
    setCustomActionInput('');
  };

  const fetchedKeysRef = useRef(new Set());

  const isNA = formik.values.is_na;
  const isProcessing = formik.isSubmitting || isUploadingAttachments;

  const actionOptions = useMemo(() => {
    const allActions = new Set([
      ...DEFAULT_ACTIONS,
      ...(filters?.distinctActions || []),
      ...customActions,
    ]);
    return Array.from(allActions)
      .sort()
      .map((act) => ({ value: act, label: act }));
  }, [filters?.distinctActions, customActions]);

  useEffect(() => {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        const decoded = jwt_decode(token);
        setUserRole(decoded.role);
      }
    } catch (err) {
      console.error('Failed to decode token:', err);
    }
  }, []);

  const isEditable = !isEdit || userRole === 'company';

  const closeModal = () => {
    setIsModalOpen(false);
    setPendingFiles([]);
    setIsUploadingAttachments(false);
    pendingFiles.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (!isEditable || isNA || isProcessing) {
        if (isNA) toast.info('Not applicable — no files needed');
        else if (!isEditable) toast.warn("You don't have permission to add attachments");
        return;
      }

      rejectedFiles.forEach(({ file, errors }) => {
        errors.forEach((err) => {
          if (err.code === 'file-too-large') toast.error(`${file.name} exceeds 120MB limit`);
          if (err.code === 'file-invalid-type') toast.error(`${file.name}: only images & videos allowed`);
        });
      });

      const currentCount = pendingFiles.length + (formik.values.attachments?.length || 0);
      const remaining = MAX_FILES - currentCount;
      const filesToAdd = acceptedFiles.slice(0, remaining);

      if (filesToAdd.length < acceptedFiles.length) {
        Swal.fire('Limit Reached', `Maximum ${MAX_FILES} files allowed`, 'warning');
      }

      if (!filesToAdd.length) return;

      const newFiles = filesToAdd.map((file) => ({
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        name: file.name,
        type: file.type,
        size: file.size,
      }));

      setPendingFiles((prev) => [...prev, ...newFiles]);
    },
    accept: ALLOWED_FILE_TYPES,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    disabled: !isEditable || isNA || isProcessing,
  });

  const removePendingFile = (index) => {
    if (!isEditable || isProcessing) return;
    setPendingFiles((prev) => {
      const file = prev[index];
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeUploadedFile = async (index) => {
    if (!isEditable || isProcessing) {
      toast.warn("Cannot modify attachments while processing");
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Attachment?',
      text: 'This will remove the attachment from the log',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
    });

    if (result.isConfirmed) {
      const newAttachments = formik.values.attachments.filter((_, i) => i !== index);
      formik.setFieldValue('attachments', newAttachments);
    }
  };

  const uploadAllPending = async (logId) => {
    if (!pendingFiles.length) return true;

    setIsUploadingAttachments(true);
    setUploadProgress({ current: 0, total: pendingFiles.length, fileName: '' });
    let successCount = 0;

    for (let i = 0; i < pendingFiles.length; i++) {
      const { file, name } = pendingFiles[i];
      setUploadProgress({ current: i + 1, total: pendingFiles.length, fileName: name });

      try {
        const { presignedUrl, key } = await requestUploadUrl(logId, name, file.type);
        await fetch(presignedUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        await confirmUpload(logId, key);

        formik.setFieldValue('attachments', [
          ...(formik.values.attachments || []),
          key,
        ]);

        successCount++;
      } catch (err) {
        console.error(`Upload failed for ${name}:`, err);
        toast.error(`Failed to upload ${name}`);
      }
    }

    setPendingFiles([]);
    setUploadProgress({ current: 0, total: 0, fileName: '' });
    setIsUploadingAttachments(false);

    return successCount === pendingFiles.length;
  };

  const handleSubmit = async () => {
    if (isProcessing) return;
    const valid = await validateStep(SnowIceRemovalLogValidationSchema, formik.values, {
      pendingFilesCount: pendingFiles.length,
    });
    if (!valid) return;
    formik.setSubmitting(true);
    try {
      dispatch(setLoading(true));
      const logId = await submitSnowLog(formik.values);

      let allAttachmentsUploaded = true;
      if (pendingFiles.length > 0) {
        toast.info("Uploading attachments...");
        allAttachmentsUploaded = await uploadAllPending(logId);
        if (!allAttachmentsUploaded) {
          toast.warning("Some attachments failed to upload, but log was saved.");
        }
      }

      await sendSnowLogNotification(logId);

      toast.success(isEdit ? "Log updated successfully" : "Log created successfully");
      setIsModalOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Failed to complete snow log process:', error);
      toast.error("Failed to save snow & ice removal log");
    } finally {
      formik.setSubmitting(false);
      setIsUploadingAttachments(false);
      dispatch(setLoading(false));
    }
  };

  const openCarousel = (startIndex) => {
    setCarouselStartIndex(startIndex);
    setCarouselOpen(true);
  };

  const carouselItems = useMemo(() => {
    const uploaded = (formik.values.attachments || []).map((att, idx) => {
      const key = typeof att === 'string' ? att : att.key || att;
      const url = signedUrlCache[key] || 'https://via.placeholder.com/800x500?text=Loading...';
      const isVideo = /\.(mp4|mov|webm|avi|mkv)$/i.test(key);
      return {
        src: url,
        caption: `Image ${idx + 1}${isVideo ? ' (Video)' : ''}`,
        type: isVideo ? 'video' : 'image',
      };
    });

    const pending = pendingFiles.map((f, idx) => ({
      src: f.preview || 'https://via.placeholder.com/800x500?text=Pending...',
      caption: `Image ${uploaded.length + idx + 1}${f.type.startsWith('video/') ? ' (Video)' : ''}`,
      type: f.type.startsWith('video/') ? 'video' : 'image',
    }));

    return [...uploaded, ...pending];
  }, [formik.values.attachments, pendingFiles, signedUrlCache]);

  useEffect(() => {
    const attachments = formik.values.attachments || [];
    const missingKeys = attachments.filter(
      (k) => !signedUrlCache[k] && !fetchedKeysRef.current.has(k)
    );

    if (!missingKeys.length) return;

    missingKeys.forEach((k) => fetchedKeysRef.current.add(k));

    getAttachmentSignedUrls(missingKeys)
      .then((urls) => setSignedUrlCache((prev) => ({ ...prev, ...urls })))
      .catch(() => toast.error('Some attachment previews could not be loaded'));
  }, [formik.values.attachments, signedUrlCache]);

  useEffect(() => {
    return () => {
      pendingFiles.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    };
  }, [pendingFiles]);

  const hasDate = !!formik.values.date;

  return (
    <>
      <Modal isOpen={isModalOpen} toggle={closeModal} size="lg" centered scrollable>
        <div
          className="d-flex justify-content-between align-items-center px-4 py-3"
          style={{ backgroundColor: '#0D568F' }}
        >
          <h5 className="mb-0 text-white">
            {isEdit ? 'Edit' : 'Create'} Snow & Ice Removal Log
          </h5>
          <img src="/images/rentdigicare-logo.svg" alt="logo" style={{ height: '34px' }} />
        </div>

        <ModalBody className="px-4 py-4">
          {!hasDate ? (
            <Alert color="warning" className="border-0 shadow-sm">
              <div className="d-flex align-items-center gap-2">
                <FeatherIcon icon="alert-circle" size={20} />
                <span>Please select a date first.</span>
              </div>
            </Alert>
          ) : (
            <>
              {isEdit && userRole !== 'company' && (
                <Alert color="info" className="border-0 shadow-sm mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <FeatherIcon icon="info" size={20} />
                    <span>Only company users can edit snow logs. You are viewing in read-only mode.</span>
                  </div>
                </Alert>
              )}

              <div className="mb-4">
                <Label className="d-flex align-items-center justify-content-between w-100">
                  <div className="d-flex align-items-center gap-2">
                    <FeatherIcon icon="calendar" size={18} className="text-primary" />
                    <h6 className="mb-0 text-dark">
                      Date & Time <span className="text-danger">*</span>
                    </h6>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="form-label mb-0">Not Applicable?</span>
                    <Input
                      type="checkbox"
                      checked={isNA}
                      onChange={(e) => formik.setFieldValue('is_na', e.target.checked)}
                      disabled={!isEditable || isProcessing}
                    />
                  </div>
                </Label>

                <Row className="g-3">
                  <Col xs={12} md={4}>
                    <FormGroup className="mb-0">
                      <Label className="small fw-semibold text-muted mb-1">
                        Date <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="date"
                        name="date"
                        value={formik.values.date || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={!isEditable || isProcessing}
                        invalid={formik.touched.date && !!formik.errors.date}
                        className="border-2"
                        style={{ borderRadius: '8px' }}
                        max={new Date().toISOString().split('T')[0]}
                      />
                      {formik.touched.date && formik.errors.date && (
                        <small className="text-danger">{formik.errors.date}</small>
                      )}
                    </FormGroup>
                  </Col>

                  {!isNA && (
                    <>
                      <Col xs={6} md={4}>
                        <FormGroup className="mb-0">
                          <Label className="small fw-semibold text-muted mb-1">
                            Time Started <span className="text-danger">*</span>
                          </Label>
                          <TimeSelect
                            value={formik.values.time_started || ''}
                            onChange={(time) => formik.setFieldValue('time_started', time)}
                            placeholder="Select start time"
                            isDisabled={!isEditable || isProcessing}
                            error={formik.touched.time_started && !!formik.errors.time_started}
                            helperText={formik.touched.time_started && formik.errors.time_started}
                          />
                        </FormGroup>
                      </Col>

                      <Col xs={6} md={4}>
                        <FormGroup className="mb-0">
                          <Label className="small fw-semibold text-muted mb-1">
                            Time Completed <span className="text-danger">*</span>
                          </Label>
                          <TimeSelect
                            value={formik.values.time_completed || ''}
                            onChange={(time) => formik.setFieldValue('time_completed', time)}
                            placeholder="Select end time"
                            isDisabled={!isEditable || isProcessing}
                            error={formik.touched.time_completed && !!formik.errors.time_completed}
                            helperText={formik.touched.time_completed && formik.errors.time_completed}
                          />
                        </FormGroup>
                      </Col>
                    </>
                  )}
                </Row>
              </div>

              <div className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <FeatherIcon icon="check-square" size={18} className="text-success" />
                  <h6 className="mb-0 text-dark">
                    Tasks Performed {!isNA && <span className="text-danger">*</span>}
                  </h6>
                </div>

                {isNA ? (
                  <Alert color="light" className="text-muted small">
                    Not applicable — no tasks required
                  </Alert>
                ) : (
                  <Row className="g-2">
                    {['sidewalks', 'all_entrances', 'stairs', 'ramp', 'amenity', 'others'].map((task) => {
                      const isChecked = formik.values.tasks?.[task] || false;
                      const taskLabel = task.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

                      return (
                        <Col xs={6} md={4} key={task}>
                          <div
                            className={`p-3 rounded-3 border-2 transition-all cursor-pointer ${isChecked
                              ? 'border-success bg-success bg-opacity-10 shadow-sm'
                              : 'border-light bg-light hover-border-primary'
                              }`}
                            style={{ cursor: isEditable && !isProcessing ? 'pointer' : 'default' }}
                            onClick={() =>
                              isEditable && !isProcessing &&
                              formik.setFieldValue(`tasks.${task}`, !isChecked)
                            }
                          >
                            <div className="d-flex align-items-center w-100">
                              <Input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => formik.setFieldValue(`tasks.${task}`, e.target.checked)}
                                disabled={!isEditable || isProcessing}
                                className="me-3 mt-0"
                                style={{ cursor: 'pointer' }}
                              />
                              <Label
                                className="mb-0 fw-medium small"
                                style={{
                                  cursor: isEditable && !isProcessing ? 'pointer' : 'default',
                                  flex: 1
                                }}
                              >
                                {taskLabel}
                              </Label>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </div>

              <div className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <FeatherIcon icon="activity" size={18} className="text-info" />
                  <h6 className="mb-0 text-dark">
                    Actions Performed {!isNA && <span className="text-danger">*</span>}
                  </h6>
                </div>

                {isNA ? (
                  <Alert color="light" className="text-muted small">
                    Not applicable — no actions required
                  </Alert>
                ) : (
                  <>
                    <Row className="g-2 mb-3">
                      {actionOptions.map((action) => {
                        const isChecked = formik.values.actions_performed?.includes(action.value);
                        return (
                          <Col xs={6} md={4} key={action.value}>
                            <div
                              className={`p-3 rounded-3 border-2 transition-all cursor-pointer ${isChecked
                                ? 'border-info bg-info bg-opacity-10 shadow-sm'
                                : 'border-light bg-light hover-border-primary'
                                }`}
                              style={{ cursor: isEditable && !isProcessing ? 'pointer' : 'default' }}
                              onClick={() => {
                                if (!isEditable || isProcessing) return;
                                const current = formik.values.actions_performed || [];
                                const updated = isChecked
                                  ? current.filter((a) => a !== action.value)
                                  : [...current, action.value];
                                formik.setFieldValue('actions_performed', updated);
                              }}
                            >
                              <div className="d-flex align-items-center w-100">
                                <Input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    const current = formik.values.actions_performed || [];
                                    const updated = e.target.checked
                                      ? [...current, action.value]
                                      : current.filter((a) => a !== action.value);
                                    formik.setFieldValue('actions_performed', updated);
                                  }}
                                  disabled={!isEditable || isProcessing}
                                  className="me-3 mt-0"
                                  style={{ cursor: 'pointer' }}
                                />
                                <Label
                                  className="mb-0 fw-medium small"
                                  style={{
                                    cursor: isEditable && !isProcessing ? 'pointer' : 'default',
                                    flex: 1,
                                  }}
                                >
                                  {action.label}
                                </Label>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>

                    {isEditable && (
                      <div className="mt-3">
                        <div className="d-flex gap-2">
                          <Input
                            type="text"
                            placeholder="Add custom action..."
                            value={customActionInput}
                            onChange={(e) => setCustomActionInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && customActionInput.trim()) {
                                handleAddCustomAction();
                              }
                            }}
                            disabled={isProcessing}
                            className="border-2"
                            style={{ borderRadius: '8px' }}
                          />
                          <Button
                            color="info"
                            onClick={handleAddCustomAction}
                            disabled={!customActionInput.trim() || isProcessing}
                            style={{ borderRadius: '8px', minWidth: '100px' }}
                          >
                            <FeatherIcon icon="plus" size={16} className="me-1" />
                            Add
                          </Button>
                        </div>
                      </div>
                    )}

                    {formik.touched.actions_performed && formik.errors.actions_performed && (
                      <small className="text-danger d-block mt-2">
                        {formik.errors.actions_performed}
                      </small>
                    )}
                  </>
                )}
              </div>

              <FormGroup className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="p-2 bg-info bg-opacity-10 rounded-3">
                    <FeatherIcon icon="cloud-snow" size={18} className="text-info" />
                  </div>
                  <Label className="small fw-semibold text-muted mb-0">Temperature & Conditions</Label>
                </div>
                <Input
                  type="text"
                  name="temp_and_conditions"
                  value={formik.values.temp_and_conditions || ''}
                  onChange={formik.handleChange}
                  disabled={!isEditable || isProcessing}
                  placeholder="Weather conditions, snow depth, ice type..."
                  className="border-2"
                  style={{ borderRadius: '8px' }}
                />
              </FormGroup>

              <FormGroup className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="p-2 bg-warning bg-opacity-10 rounded-3">
                    <FeatherIcon icon="message-circle" size={18} className="text-warning" />
                  </div>
                  <Label className="small fw-semibold text-muted mb-0">Comments</Label>
                </div>
                <Input
                  type="text"
                  name="comments"
                  value={formik.values.comments || ''}
                  onChange={formik.handleChange}
                  disabled={!isEditable || isProcessing}
                  placeholder="Challenges, observations, notes..."
                  className="border-2"
                  style={{ borderRadius: '8px' }}
                />
              </FormGroup>

              <div className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <FeatherIcon icon="image" size={18} className="text-primary" />
                  <h6 className="mb-0 text-dark">
                    Supporting Media (Images & Videos) {!isNA && <span className="text-danger">*</span>}
                  </h6>
                  {!isNA && (
                    <Badge color="light" className="text-muted ms-2">
                      {(pendingFiles.length + (formik.values.attachments?.length || 0))}/{MAX_FILES}
                    </Badge>
                  )}
                </div>

                {isNA ? (
                  <Alert color="light" className="text-muted small">
                    Not applicable — no media required
                  </Alert>
                ) : isEditable ? (
                  <>
                    <div
                      {...getRootProps()}
                      className={`rounded-3 p-4 text-center transition-all ${isDragActive ? 'border-2 border-primary bg-primary bg-opacity-10' : 'border-2 border-dashed border-secondary bg-light'
                        }`}
                      style={{ cursor: isEditable && !isProcessing ? 'pointer' : 'default' }}
                    >
                      <input {...getInputProps()} />
                      <div className={`mb-3 ${isDragActive ? 'animate__animated animate__pulse' : ''}`}>
                        <FeatherIcon
                          icon="upload-cloud"
                          size={40}
                          className={isDragActive ? 'text-primary' : 'text-secondary'}
                        />
                      </div>
                      <p className="mb-1 fw-semibold" style={{ fontSize: '14px' }}>
                        {isDragActive ? 'Drop files here' : 'Drag & drop or click to upload'}
                      </p>
                      <small className="text-muted">Images & Videos • Max 120MB each</small>

                      {isUploadingAttachments && uploadProgress.total > 0 && (
                        <div className="mt-3">
                          <Progress
                            value={(uploadProgress.current / uploadProgress.total) * 100}
                            animated
                            color="success"
                            style={{ height: '6px', borderRadius: '10px' }}
                          />
                          <small className="d-block mt-2 text-muted">
                            Uploading {uploadProgress.current}/{uploadProgress.total} — {uploadProgress.fileName}
                          </small>
                        </div>
                      )}
                    </div>

                    {pendingFiles.length > 0 && (
                      <div className="mt-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Badge color="warning" pill>Pending</Badge>
                          <small className="text-muted">{pendingFiles.length} file(s)</small>
                        </div>
                        <Row className="g-2">
                          {pendingFiles.map((f, i) => (
                            <Col xs={4} sm={3} key={i}>
                              <div
                                className="position-relative rounded-3 overflow-hidden shadow-sm"
                                style={{ height: '90px', cursor: 'pointer' }}
                                onClick={() => openCarousel(i + (formik.values.attachments?.length || 0))}
                              >
                                {f.type.startsWith('image/') ? (
                                  <img
                                    src={f.preview}
                                    alt={f.name}
                                    className="w-100 h-100"
                                    style={{ objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div
                                    className="d-flex align-items-center justify-content-center h-100"
                                    style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                                  >
                                    <FeatherIcon icon="video" size={28} className="text-white" />
                                  </div>
                                )}
                                <Button
                                  size="sm"
                                  color="danger"
                                  className="position-absolute top-0 end-0 rounded-circle m-2 p-0 shadow-sm"
                                  style={{ width: '24px', height: '24px' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removePendingFile(i);
                                  }}
                                  disabled={isProcessing}
                                >
                                  <FeatherIcon icon="x" size={14} />
                                </Button>
                                <div
                                  className="position-absolute bottom-0 start-0 end-0 p-1"
                                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }}
                                >
                                  <small className="text-white d-block text-truncate" style={{ fontSize: '10px' }}>
                                    Pending {i + 1}
                                  </small>
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}

                    {formik.values.attachments?.length > 0 && (
                      <div className="mt-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Badge color="success" pill>Uploaded</Badge>
                          <small className="text-muted">{formik.values.attachments.length} file(s)</small>
                        </div>
                        <Row className="g-2">
                          {formik.values.attachments.map((att, i) => {
                            const key = typeof att === 'string' ? att : att.key || att;
                            const url = signedUrlCache[key];
                            const isVideo = /\.(mp4|mov|webm|avi|mkv)$/i.test(key);

                            return (
                              <Col xs={4} sm={3} key={i}>
                                <div
                                  className="position-relative rounded-3 overflow-hidden shadow-sm"
                                  style={{ height: '90px', cursor: url ? 'pointer' : 'default' }}
                                  onClick={() => url && openCarousel(i)}
                                >
                                  {url ? (
                                    isVideo ? (
                                      <div
                                        className="d-flex align-items-center justify-content-center h-100"
                                        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                                      >
                                        <FeatherIcon icon="play-circle" size={32} className="text-white" />
                                      </div>
                                    ) : (
                                      <img
                                        src={url}
                                        alt={`Image ${i + 1}`}
                                        className="w-100 h-100"
                                        style={{ objectFit: 'cover' }}
                                      />
                                    )
                                  ) : (
                                    <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                                      <Spinner size="sm" color="primary" />
                                    </div>
                                  )}
                                  <Button
                                    size="sm"
                                    color="danger"
                                    className="position-absolute top-0 end-0 rounded-circle m-2 p-0 shadow-sm"
                                    style={{ width: '24px', height: '24px' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeUploadedFile(i);
                                    }}
                                    disabled={isProcessing}
                                  >
                                    <FeatherIcon icon="x" size={14} />
                                  </Button>
                                  <div
                                    className="position-absolute bottom-0 start-0 end-0 p-1"
                                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }}
                                  >
                                    <small className="text-white d-block text-truncate" style={{ fontSize: '10px' }}>
                                      Image {i + 1}
                                    </small>
                                  </div>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    )}

                    {pendingFiles.length > 0 && (
                      <Alert color="info" className="mt-3 mb-0">
                        <div className="d-flex align-items-center">
                          <FeatherIcon icon="cloud-upload" size={18} className="me-2" />
                          <span>{pendingFiles.length} file(s) pending upload — will be uploaded when you save</span>
                        </div>
                      </Alert>
                    )}
                  </>
                ) : (
                  formik.values.attachments?.length > 0 && (
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                          <FeatherIcon icon="image" size={18} className="text-primary" />
                        </div>
                        <h6 className="mb-0 text-dark">Uploaded Files</h6>
                        <Badge color="light" className="text-muted">
                          {formik.values.attachments.length}
                        </Badge>
                      </div>
                      <Row className="g-2">
                        {formik.values.attachments.map((att, i) => {
                          const key = typeof att === 'string' ? att : att.key || att;
                          const url = signedUrlCache[key];
                          const isVideo = /\.(mp4|mov|webm|avi|mkv)$/i.test(key);

                          return (
                            <Col xs={4} sm={3} key={i}>
                              <div
                                className="rounded-3 overflow-hidden shadow-sm position-relative"
                                style={{ height: '90px', cursor: url ? 'pointer' : 'default' }}
                                onClick={() => url && openCarousel(i)}
                              >
                                {url ? (
                                  isVideo ? (
                                    <div
                                      className="d-flex align-items-center justify-content-center h-100"
                                      style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                                    >
                                      <FeatherIcon icon="play-circle" size={32} className="text-white" />
                                    </div>
                                  ) : (
                                    <img
                                      src={url}
                                      alt={`Image ${i + 1}`}
                                      className="w-100 h-100"
                                      style={{ objectFit: 'cover' }}
                                    />
                                  )
                                ) : (
                                  <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                                    <Spinner size="sm" color="primary" />
                                  </div>
                                )}
                                <div
                                  className="position-absolute bottom-0 start-0 end-0 p-1"
                                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }}
                                >
                                  <small className="text-white d-block text-truncate" style={{ fontSize: '10px' }}>
                                    Image {i + 1}
                                  </small>
                                </div>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </ModalBody>

        <ModalFooter className="border-0 pt-0 px-4 pb-4">
          {hasDate ? (
            <div className="d-flex gap-2 w-100">
              <Button
                color="secondary"
                onClick={closeModal}
                disabled={isProcessing}
                className="flex-grow-1"
                style={{ borderRadius: '8px' }}
              >
                Cancel
              </Button>
              {isEditable && (
                <Button
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    borderRadius: '8px',
                  }}
                  onClick={handleSubmit}
                  disabled={isProcessing}
                  className="flex-grow-1 text-white"
                >
                  {isProcessing ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {isUploadingAttachments ? 'Uploading attachments...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <FeatherIcon icon="check" size={16} className="me-2" />
                      {isEdit ? 'Update' : 'Create'}
                    </>
                  )}
                </Button>
              )}
            </div>
          ) : (
            <Button color="secondary" onClick={closeModal} size="sm">
              Close
            </Button>
          )}
        </ModalFooter>
      </Modal>

      <AttachmentCarouselModal
        isOpen={carouselOpen}
        toggle={() => setCarouselOpen(false)}
        items={carouselItems}
        initialIndex={carouselStartIndex}
      />
    </>
  );
};

export { GenerateSnowIceRemovalLogModal };